import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type RailbarPosition = "header" | "content";

interface RailbarPortalContextValue {
  getTarget: (position: RailbarPosition) => HTMLElement | null;
  registerTarget: (position: RailbarPosition, el: HTMLElement | null) => void;
}

const RailbarPortalContext = createContext<RailbarPortalContextValue | null>(
  null,
);
RailbarPortalContext.displayName = "RailbarPortalContext";

/**
 * Scoped railbar portal target for modal/non-shell consumers that need to
 * mount their own railbar without losing the global `#rail-bar-header` ID
 * race against an outer container (e.g. the AppShell's empty rail panel
 * or any other layout that also renders that id).
 *
 * Pair this provider with a `<RailbarContainer />` inside the subtree that
 * should own the portal target. `RailBarPortal` will prefer the context
 * target when one is registered and fall back to
 * `document.querySelector('#rail-bar-{position}')` otherwise — so
 * consumers outside any provider keep working unchanged.
 */
export const RailbarPortalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [headerTarget, setHeaderTarget] = useState<HTMLElement | null>(null);
  const [contentTarget, setContentTarget] = useState<HTMLElement | null>(null);

  const registerTarget = useCallback(
    (position: RailbarPosition, el: HTMLElement | null) => {
      if (position === "header") {
        setHeaderTarget(el);
        return;
      }
      setContentTarget(el);
    },
    [],
  );

  const getTarget = useCallback(
    (position: RailbarPosition) =>
      position === "header" ? headerTarget : contentTarget,
    [headerTarget, contentTarget],
  );

  const value = useMemo(
    () => ({ getTarget, registerTarget }),
    [getTarget, registerTarget],
  );

  return (
    <RailbarPortalContext.Provider value={value}>
      {children}
    </RailbarPortalContext.Provider>
  );
};

export const useRailbarPortalContext = () => useContext(RailbarPortalContext);
